import Vue from 'vue';

import {
  normalizeResearchClient,
  normalizeResearchUnifiedSearchItem,
  normalizeResearchUnifiedSearchItems,
} from './normalizers';

const decrementTotalCount = meta => {
  if (!meta || typeof meta.total_count !== 'number') {
    return meta;
  }
  return {
    ...meta,
    total_count: Math.max(0, meta.total_count - 1),
  };
};

const getSharepointFilesCount = data => {
  if (Number.isFinite(data?.files_count)) {
    return data.files_count;
  }
  if (Array.isArray(data?.objects)) {
    return data.objects.length;
  }
  return 0;
};

const getArchiveFilesCount = data => {
  if (Number.isFinite(data?.files_count)) {
    return data.files_count;
  }
  if (Array.isArray(data?.objects)) {
    return data.objects.length;
  }
  return 0;
};

export default {
  upsertResearchMemberState(state, membership) {
    if (['hidden', 'completed'].includes(`${membership?.status || ''}`)) {
      const clientId = `${membership?.research_client?.id || membership?.research_client_id || ''}`;
      if (clientId) {
        state.researcherClients = state.researcherClients.filter(client => `${client.id}` !== clientId);
      }
      return;
    }
    const client = normalizeResearchClient(membership?.research_client, membership);
    if (!client) {
      return;
    }
    const existingIndex = state.researcherClients.findIndex(
      existingClient => `${existingClient.id}` === `${client.id}`
    );
    if (existingIndex === -1) {
      state.researcherClients = [...state.researcherClients, client];
    } else {
      const nextClients = [...state.researcherClients];
      nextClients.splice(existingIndex, 1, {...nextClients[existingIndex], ...client});
      state.researcherClients = nextClients;
    }
    client.projects_list.forEach(project => {
      Vue.set(state.researcherProjectsById, project.id, project);
    });
  },
  removeResearchMemberState(state, clientId) {
    state.researcherClients = state.researcherClients.filter(client => `${client.id}` !== `${clientId}`);
  },
  upsertResearchClientState(state, client) {
    if (!client?.id) {
      return;
    }

    const projectList = Array.isArray(client.projects) ? client.projects.filter(project => project?.id) : [];
    const projectIds = projectList.map(project => project.id);
    const normalizedClient = {
      ...client,
      projects: projectIds,
      projects_list: projectList,
    };
    state.researcherProjectsById = state.researcherProjectsById || {};
    projectList.forEach(project => {
      Vue.set(state.researcherProjectsById, project.id, project);
    });

    const assignedClientIndex = state.researcherClients.findIndex(
      existingClient => `${existingClient.id}` === `${client.id}`
    );
    if (assignedClientIndex !== -1) {
      const nextClients = [...state.researcherClients];
      nextClients.splice(assignedClientIndex, 1, {
        ...nextClients[assignedClientIndex],
        ...normalizedClient,
        membership_id: nextClients[assignedClientIndex].membership_id,
        membership_status: nextClients[assignedClientIndex].membership_status,
      });
      state.researcherClients = nextClients;
    }

    const otherSelectedClients = state.savedClients?.otherSelectedClients || [];
    const otherClientIndex = otherSelectedClients.findIndex(
      existingClient => `${existingClient.id}` === `${client.id}`
    );
    if (otherClientIndex === -1) {
      return;
    }

    const nextOtherClients = [...otherSelectedClients];
    nextOtherClients.splice(otherClientIndex, 1, {
      ...nextOtherClients[otherClientIndex],
      ...client,
    });
    state.savedClients = {
      ...state.savedClients,
      otherSelectedClients: nextOtherClients,
    };
  },
  setResearcherClientsState(state, {memberships} = {}) {
    const normalizedMemberships = Array.isArray(memberships) ? memberships : [];
    const clients = [];
    const projectsById = {};

    normalizedMemberships.forEach(membership => {
      const client = normalizeResearchClient(membership?.research_client, membership);
      if (!client) {
        return;
      }
      clients.push(client);
      client.projects_list.forEach(project => {
        projectsById[project.id] = project;
      });
    });

    state.researcherClients = clients;
    state.researcherProjectsById = projectsById;
  },
  setResearchNotesFilterOptionsForScopeState(state, {scopeKey, tags, authors, projects} = {}) {
    if (!scopeKey) {
      return;
    }
    Vue.set(state.notesFilterOptionsByScope, scopeKey, {
      tags: Array.isArray(tags) ? tags : [],
      authors: Array.isArray(authors) ? authors : [],
      projects: Array.isArray(projects) ? projects : [],
    });
  },
  setResearchSharepointFilesForClientState(state, {clientId, data} = {}) {
    if (!clientId) {
      return;
    }
    Vue.set(state.sharepointFilesByClient, `${clientId}`, {
      objects: Array.isArray(data?.objects) ? data.objects : [],
      folder_path: data?.folder_path || '',
      sharepoint_url: data?.sharepoint_url || '',
      files_count: getSharepointFilesCount(data),
      fetched_at: data?.fetched_at || Date.now(),
    });
  },
  setResearchSharepointFilesLoadingForClientState(state, {clientId, isLoading} = {}) {
    if (!clientId) {
      return;
    }
    Vue.set(state.sharepointFilesLoadingByClient, `${clientId}`, !!isLoading);
  },
  setResearchSharepointFilesErrorForClientState(state, {clientId, error} = {}) {
    if (!clientId) {
      return;
    }
    if (!error) {
      Vue.delete(state.sharepointFilesErrorByClient, `${clientId}`);
      return;
    }
    Vue.set(state.sharepointFilesErrorByClient, `${clientId}`, error);
  },
  setResearchArchiveFilesForClientState(state, {clientId, data} = {}) {
    if (!clientId) {
      return;
    }
    Vue.set(state.archiveFilesByClient, `${clientId}`, {
      objects: Array.isArray(data?.objects) ? data.objects : [],
      archive_bucket: data?.archive_bucket || '',
      archive_prefix: data?.archive_prefix || '',
      folder_path: data?.folder_path || '',
      files_count: getArchiveFilesCount(data),
      fetched_at: data?.fetched_at || Date.now(),
    });
  },
  setResearchArchiveFilesLoadingForClientState(state, {clientId, isLoading} = {}) {
    if (!clientId) {
      return;
    }
    Vue.set(state.archiveFilesLoadingByClient, `${clientId}`, !!isLoading);
  },
  setResearchArchiveFilesErrorForClientState(state, {clientId, error} = {}) {
    if (!clientId) {
      return;
    }
    if (!error) {
      Vue.delete(state.archiveFilesErrorByClient, `${clientId}`);
      return;
    }
    Vue.set(state.archiveFilesErrorByClient, `${clientId}`, error);
  },
  setActiveResearchNotesMemberIdState(state, memberKey) {
    state.activeNotesMemberId = memberKey || '';
  },
  setResearchNotesPageState(state, {notes, meta} = {}) {
    state.notes = Array.isArray(notes) ? notes : [];
    state.notesMeta = meta || {};
  },
  clearActiveResearchNotesState(state) {
    state.notes = [];
    state.notesMeta = {};
  },
  setResearchNotesState(state, notes) {
    state.notes = notes;
  },
  mutateResearchNotesState(state, {noteId, title, content, content_json, location, tags, projects}) {
    const index = state.notes.findIndex(n => n.id === noteId);
    if (index !== -1) {
      if (typeof title !== 'undefined') {
        Vue.set(state.notes[index], 'title', title);
      }
      if (typeof content !== 'undefined') {
        Vue.set(state.notes[index], 'content', content);
      }
      if (typeof content_json !== 'undefined') {
        Vue.set(state.notes[index], 'content_json', content_json);
      }
      if (typeof location !== 'undefined') {
        Vue.set(state.notes[index], 'location', location);
      }
      if (typeof tags !== 'undefined') {
        Vue.set(state.notes[index], 'tags', tags);
      }
      if (typeof projects !== 'undefined') {
        Vue.set(state.notes[index], 'projects', projects);
      }
    }
  },
  setResearchNotesMetaState(state, notesMeta) {
    state.notesMeta = notesMeta;
  },
  removeResearchNoteState(state, noteId) {
    state.notes = Array.isArray(state.notes) ? state.notes.filter(note => `${note.id}` !== `${noteId}`) : [];
    state.notesMeta = decrementTotalCount(state.notesMeta);
  },
  setResearchNotesLoadingState(state, isLoading) {
    state.notesLoading = isLoading;
  },
  setResearchMembersUpdatingState(state, isLoading) {
    state.researchMembersUpdating = isLoading;
  },
  setResearchNotesLoadingRequestId(state, requestId) {
    state.notesLoadingRequestId = requestId;
  },
  setResearchNoteState(state, note) {
    state.note = note;
  },
  clearResearchNoteState(state, noteId) {
    if (!state.note) {
      return;
    }
    if (
      typeof noteId === 'undefined' ||
      `${state.note.id}` === `${noteId}` ||
      `${state.note.object_id}` === `${noteId}`
    ) {
      state.note = null;
    }
  },
  mutateResearchNoteAssetsState(state, asset) {
    if (!state.note || !Array.isArray(state.note.assets)) {
      state.note = {...(state.note || {}), assets: []};
    }
    state.note.assets.unshift(asset);
  },
  setResearchNoteLoadingState(state, isLoading) {
    state.noteLoading = isLoading;
  },
  setResearchNoteUpdateLoadingState(state, data) {
    state.noteUpdateLoading = data;
  },
  setResearchUnifiedSearchListState(state, list) {
    state.unifiedSearchList = normalizeResearchUnifiedSearchItems(list);
  },
  setResearchUnifiedSearchMetaState(state, meta) {
    state.unifiedSearchMeta = meta;
  },
  setResearchUnifiedSearchLoadingState(state, isLoading) {
    state.unifiedSearchLoading = isLoading;
  },
  mutateResearchUnifiedSearchItemState(state, payload) {
    const list = state.unifiedSearchList || [];
    const noteId = payload?.object_id || payload?.id;
    if (!noteId) {
      return;
    }
    const index = list.findIndex(item => item.id === noteId);
    if (index === -1) {
      return;
    }
    const updatedItem = normalizeResearchUnifiedSearchItem(payload, list[index]);
    const nextList = [...list];
    nextList.splice(index, 1, updatedItem);
    state.unifiedSearchList = nextList;
  },
  removeResearchUnifiedSearchItemState(state, noteId) {
    const list = Array.isArray(state.unifiedSearchList) ? state.unifiedSearchList : [];
    state.unifiedSearchList = list.filter(item => `${item.id}` !== `${noteId}`);
    state.unifiedSearchMeta = decrementTotalCount(state.unifiedSearchMeta);
  },
  setResearchSavedClientsState(state, payload) {
    state.savedClients = {
      ...state.savedClients,
      ...payload,
    };
  },
};
